import { createApi } from '@reduxjs/toolkit/query/react'
import axiosBaseQuery from 'services/axiosBaseQuery'
import { sendNotification } from 'components'
import {
  AddCaseRequest,
  CaseCustomFieldsRequest,
  CaseDetailRequest,
  CaseDetailResponse,
  CaseListRequest,
  CaseListResponse,
  CaseTypesRequest,
  CaseTypesResponse,
  CommentListRequest,
  CommentListResponse,
  CustomFieldEngResponse,
  DeleteCommentRequest,
  ExportRequest,
  MutationResponse,
  TableResponseWithTimezone,
  UpdateStatusRequest,
} from './types'
import { sendExportNotification, sendImportNotification } from 'utils'
import { GenericResponse, TableRequestV2 } from 'services/types'
import { reportsApi } from 'services/ReportManagement/reports'

export const caseApi = createApi({
  reducerPath: 'caseApi',
  tagTypes: ['Cases', 'Comments', 'CaseDetail', 'CaseHistory', 'CaseTypes'],
  baseQuery: axiosBaseQuery({
    baseUrl: `${window.envVariables.REACT_APP_BASE_URL + '/engagement/'}`,
  }),
  endpoints: (builder) => ({
    getCases: builder.query<CaseListResponse, CaseListRequest>({
      query: ({ client_id, ...data }) => ({
        url: `clients/${client_id}/case/list/`,
        method: 'POST',
        data,
      }),
      providesTags: ['Cases'],
      transformResponse: (response: CaseListResponse) => {
        const enhancedColumns = response.columns.map((col) => {
          if (col.accessor === 'additional_information') {
            return {
              ...col,
              width: 85,
            }
          }
          return col
        })

        return {
          ...response,
          columns: enhancedColumns,
        }
      },
    }),
    getComments: builder.query<CommentListResponse, CommentListRequest>({
      query: ({ client_id, ...data }) => ({
        url: `clients/${client_id}/comment/list/`,
        method: 'POST',
        data,
      }),
      providesTags: ['Comments'],
    }),
    getCaseDetail: builder.query<CaseDetailResponse, CaseDetailRequest>({
      query: ({ client_id, case_id }) => ({
        url: `clients/${client_id}/case/?case_id=${case_id}`,
        method: 'GET',
      }),
      providesTags: ['CaseDetail'],
    }),
    getCaseDynamicFields: builder.query<CustomFieldEngResponse, CaseCustomFieldsRequest>({
      query: ({ client_id, case_type_id }) => ({
        url: `clients/${client_id}/case_dynamic_fields/`,
        method: 'GET',
        params: {
          case_type_id,
        },
      }),
    }),
    addCase: builder.mutation<MutationResponse, AddCaseRequest>({
      query: ({ client_id, body }) => ({
        url: `clients/${client_id}/case/`,
        method: 'POST',
        data: body,
      }),
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })

          dispatch(caseApi.util.invalidateTags(['Cases', 'CaseHistory'])) // use this instead of invalidateTags to cross tab sync
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    editCase: builder.mutation<MutationResponse, AddCaseRequest & { case_id: string }>({
      query: ({ client_id, body, case_id }) => ({
        url: `clients/${client_id}/case/${case_id}/`,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['Cases', 'CaseDetail', 'CaseHistory'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    exportCase: builder.mutation<MutationResponse, ExportRequest>({
      query: ({ client_id, ...data }) => ({
        url: `clients/${client_id}/case/export/`,
        method: 'POST',
        data,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          sendExportNotification(data.detail.message)
          dispatch(reportsApi.util.invalidateTags(['ExportReports']))
        } catch (error) {}
      },
    }),
    addComment: builder.mutation<MutationResponse, AddCaseRequest>({
      query: ({ client_id, body }) => ({
        url: `clients/${client_id}/comment/`,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['Comments'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    editComment: builder.mutation<MutationResponse, AddCaseRequest & { comment_id: string }>({
      query: ({ client_id, body, comment_id }) => ({
        url: `clients/${client_id}/comment/${comment_id}/`,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['Comments'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    deleteComment: builder.mutation<MutationResponse, DeleteCommentRequest>({
      query: ({ client_id, comment_id }) => ({
        url: `clients/${client_id}/comment/${comment_id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Comments', 'CaseDetail'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    importCases: builder.mutation<MutationResponse, { clientId: string; body: FormData }>({
      query: ({ clientId, body }) => ({
        url: `clients/${clientId}/case/import/`,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['Cases'],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          sendImportNotification(data.detail.message)
          dispatch(reportsApi.util.invalidateTags(['ImportReports']))
        } catch (error) {}
      },
    }),
    exportCases: builder.mutation<MutationResponse, ExportRequest>({
      query: ({ client_id, ...data }) => ({
        url: `clients/${client_id}/case/export/`,
        method: 'POST',
        data,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          sendExportNotification(data.detail.message)
          dispatch(reportsApi.util.invalidateTags(['ExportReports']))
        } catch (error) {}
      },
    }),
    caseHistory: builder.query<
      TableResponseWithTimezone<{}>,
      TableRequestV2 & {
        case_id: string
      }
    >({
      query: ({ client_id, case_id, ...data }) => ({
        url: `clients/${client_id}/case/${case_id}/history/`,
        method: 'POST',
        data,
      }),
      providesTags: ['CaseHistory'],
    }),
    updateCaseStatus: builder.mutation<MutationResponse, UpdateStatusRequest>({
      query: ({ client_id, ...data }) => ({
        url: `clients/${client_id}/status/update/`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Cases', 'CaseDetail', 'CaseHistory'],
    }),
    deleteCase: builder.mutation<
      MutationResponse,
      {
        client_id: string
        case_id: string
      }
    >({
      query: ({ client_id, case_id }) => ({
        url: `clients/${client_id}/case/${case_id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Cases', 'CaseDetail'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    getCaseTypes: builder.query<CaseTypesResponse, CaseTypesRequest>({
      query: ({ client_id }) => ({
        url: `clients/${client_id}/case_type/list/`,
        method: 'GET',
      }),
      providesTags: ['CaseTypes'],
    }),
    linkUnlinkTaskToCase: builder.mutation<
      GenericResponse & { case_id: string; task_id: number },
      { client_id: string; case_id: string; task_id: number; action: 'link' | 'unlink' }
    >({
      query: ({ client_id, case_id, task_id, action }) => ({
        url: `clients/${client_id}/case/${case_id}/link_unlink_task_from_case/`,
        method: 'POST',
        data: {
          action,
          task_id,
        },
      }),
      invalidatesTags: ['Cases', 'CaseDetail', 'CaseHistory'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          console.log(err)
        }
      },
    }),
    caseMarkAsRead: builder.mutation<
      MutationResponse,
      { clientId: string; case_ids: string[]; showNotification?: boolean }
    >({
      query: ({ clientId, case_ids }) => ({
        url: `clients/${clientId}/case/mark_as_read/`,
        method: 'POST',
        data: {
          case_ids,
          is_viewed: true,
        },
      }),
      async onQueryStarted(
        { showNotification }: { showNotification?: boolean },
        { queryFulfilled, dispatch },
      ) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          dispatch(caseApi.util.invalidateTags(['Cases', 'CaseDetail'])) // use this instead of invalidateTags to cross tab sync
          showNotification &&
            sendNotification({
              type: 'success',
              toastContent: data.detail.message,
            })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    caseCommentMarkAsRead: builder.mutation<
      MutationResponse,
      { clientId: string; comment_ids?: string[]; case_ids?: string[]; showNotification?: boolean }
    >({
      query: ({ clientId, comment_ids, case_ids }) => ({
        url: `clients/${clientId}/comment/mark_as_read/`,
        method: 'POST',
        data: {
          comment_ids,
          case_ids,
          is_viewed: true,
        },
      }),
      invalidatesTags: ['Comments', 'Cases'],
      async onQueryStarted({ showNotification }, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          showNotification &&
            sendNotification({
              type: 'success',
              toastContent: data.detail.message,
            })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
  }),
})

export const {
  useGetCasesQuery,
  useGetCommentsQuery,
  useGetCaseDetailQuery,
  useGetCaseDynamicFieldsQuery,
  useAddCaseMutation,
  useEditCaseMutation,
  useAddCommentMutation,
  useEditCommentMutation,
  useDeleteCommentMutation,
  useImportCasesMutation,
  useExportCasesMutation,
  useCaseHistoryQuery,
  useUpdateCaseStatusMutation,
  useDeleteCaseMutation,
  useGetCaseTypesQuery,
  useLinkUnlinkTaskToCaseMutation,
  useCaseMarkAsReadMutation,
  useCaseCommentMarkAsReadMutation,
} = caseApi
